/**
 * Eigen lijn-SVG's voor de werkplek.
 *
 * Bewust geen emoji en geen icoonlettertype (huisregel): zestien pixels
 * lijnwerk dat de kleur van zijn chip erft. De mock gebruikt dezelfde
 * vormen uit zijn eigen sprite.
 */

interface Props {
  /** Grootte in pixels; de chip gebruikt 12, een knop 13. */
  maat?: number;
}

function Omhulsel({
  maat = 13,
  children,
  titel,
}: Props & { children: React.ReactNode; titel?: string }) {
  return (
    <svg
      width={maat}
      height={maat}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={titel ? undefined : true}
      role={titel ? "img" : undefined}
      focusable="false"
      style={{ flex: "none" }}
    >
      {titel ? <title>{titel}</title> : null}
      {children}
    </svg>
  );
}

export function IcoonMail(props: Props) {
  return (
    <Omhulsel {...props}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </Omhulsel>
  );
}

export function IcoonWhatsapp(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M21 12a9 9 0 0 1-13.4 7.8L3 21l1.2-4.6A9 9 0 1 1 21 12Z" />
      <path d="M8.5 9.5c0 3 2.5 5.5 5.5 5.5" />
    </Omhulsel>
  );
}

export function IcoonChat(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M21 12a8 8 0 0 1-8 8H7l-4 3v-6a8 8 0 0 1 8-8h2a8 8 0 0 1 8 3Z" />
    </Omhulsel>
  );
}

export function IcoonTelefoon(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M5 3h4l2 5-2.5 1.5a12 12 0 0 0 6 6L16 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 5a2 2 0 0 1 2-2Z" />
    </Omhulsel>
  );
}

export function IcoonKlok(props: Props) {
  return (
    <Omhulsel {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Omhulsel>
  );
}

/** Sessie 47 (3a): een taak, een afvinklijstje. */
export function IcoonTaak(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M3.5 6l1.5 1.5L7.5 5M3.5 12l1.5 1.5L7.5 11" />
      <circle cx="5" cy="18" r="1.2" />
    </Omhulsel>
  );
}

export function IcoonEuro(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M17 6a7 7 0 1 0 0 12" />
      <path d="M4 10h9M4 14h9" />
    </Omhulsel>
  );
}

export function IcoonSchild(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M12 3 20 6v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6Z" />
    </Omhulsel>
  );
}

export function IcoonFormulier(props: Props) {
  return (
    <Omhulsel {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </Omhulsel>
  );
}

/** Sessie 51: beeld-icoon voor "Wat ik zie" (eerste blik op een foto). */
export function IcoonBeeld(props: Props) {
  return (
    <Omhulsel {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="m21 16-5-5-9 9" />
    </Omhulsel>
  );
}

export function IcoonExtern(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </Omhulsel>
  );
}

export function IcoonKruis(props: Props) {
  return (
    <Omhulsel {...props} titel="Sluiten">
      <path d="M6 6 18 18M18 6 6 18" />
    </Omhulsel>
  );
}

export function IcoonPijlOnder(props: Props) {
  return (
    <Omhulsel {...props}>
      <path d="M6 9l6 6 6-6" />
    </Omhulsel>
  );
}

export function IcoonBeheer(props: Props) {
  return (
    <Omhulsel {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    </Omhulsel>
  );
}

/** Kanaalchip-pictogram op naam; onbekend kanaal levert niets. */
export function KanaalIcoon({ kanaal, maat = 12 }: { kanaal: string; maat?: number }) {
  switch (kanaal) {
    case "mail":
      return <IcoonMail maat={maat} />;
    case "whatsapp":
      return <IcoonWhatsapp maat={maat} />;
    case "chat":
      return <IcoonChat maat={maat} />;
    case "telefoon":
      return <IcoonTelefoon maat={maat} />;
    case "taken":
    case "taak":
      return <IcoonTaak maat={maat} />;
    default:
      return null;
  }
}
